import type { GridColDef } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import type { PbjLongShiftReviewRow, PbjShortShiftReviewRow } from "../../../utils/models";
import type { MultiSortOption } from "../../../utils/multiColumnSorting";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { dayTypeFacet, monthFacet } from "../pbjFacets";
import { sectionFrameHeight } from "../pbjGridCells";

/** The two per-shift duration audits (Long Shift Review, Short Shift Review) return an identical row
 * shape — one raw punch pair with its gross wall-clock duration — so they render through one grid. */
export type ShiftDurationReviewRow = PbjLongShiftReviewRow | PbjShortShiftReviewRow;

type Props = {
	title: string;
	/** Undefined while the threshold is still loading — the caller withholds the number-bearing subtitle
	 * rather than flashing "exceeds  hours". */
	description: string | undefined;
	emptyTitle: string;
	errorMessage: string;
	rows: ShiftDurationReviewRow[];
	loading: boolean;
	error: boolean;
	sectionTestId: string;
	tableTestId: string;
	emptyTestId: string;
	sortingLocalStorageKey?: string;
};

/** `date` arrives as a bare yyyy-MM-dd the BE already resolved in the DEPARTMENT's timezone, so it must
 * be parsed zone-free — applying a zone here would shift it off the dept-local day the BE computed. */
export const formatReviewDate = (iso: string | null): string => {
	if (!iso) return "—";
	const dt = DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
};

/**
 * Punch timestamps render in the DEPARTMENT's timezone, not the viewer's. The BE resolves each row's
 * `date` dept-locally, so rendering `startTime` in the browser zone made the row contradict itself:
 * a 22:58Z punch at a New York facility is dept-local Jul 15 6:58 PM, but a viewer in Asia/Jerusalem
 * saw Date "Jul 15" beside Start "Jul 16, 1:58 AM". The XLSX export already renders dept-local
 * (_format_local_datetime) — this brings the grid in line.
 */
export const formatReviewDateTime = (iso: string | null, zone: string): string => {
	if (!iso) return "—";
	// DEFAULT: an empty zone (the ChosenDepartment placeholder before the dept loads) falls back to the
	// viewer's zone instead of rendering "—" — dropping the timestamp entirely would be worse than a
	// momentary zone mismatch, and the audit queries are gated on a loaded department anyway.
	const dt = zone ? DateTime.fromISO(iso).setZone(zone) : DateTime.fromISO(iso);
	return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : "—";
};

export const formatReviewHours = (hours: number): string => hours.toFixed(2);

/**
 * Read-only per-shift duration audit section: one row per raw punch pair, keyed by its pbj_shift id
 * (a composite empId/start/end can collide on exactly the duplicate rows these audits surface).
 *
 * Generic over WHICH end of the duration distribution is being flagged — the caller supplies the copy
 * and the query, so the Long Shift Review (>19h) and the Short Shift Review (<1h) are the same grid
 * with different labels. Both read raw punches: no midnight-split, no break-netting.
 */
const COLUMN_FACET: Readonly<Record<string, string>> = { date: "month" };

export const ShiftDurationReviewSection: FC<Props> = ({
	title,
	description,
	emptyTitle,
	errorMessage,
	rows,
	loading,
	error,
	sectionTestId,
	tableTestId,
	emptyTestId,
	sortingLocalStorageKey,
}) => {
	const { t } = useTranslation();
	// Punch times render in the facility's zone, not the viewer's — see formatReviewDateTime.
	const { chosenDepartment } = useChosenDepartment();
	const deptTimezone = chosenDepartment.timezone;

	const columns: GridColDef<ShiftDurationReviewRow>[] = useMemo(
		() => [
			{ field: "empId", headerName: t("pbj_model.audit.long_shifts.cols.id"), minWidth: 140, flex: 1 },
			{ field: "name", headerName: t("pbj_model.audit.long_shifts.cols.name"), minWidth: 180, flex: 1 },
			{
				field: "date",
				headerName: t("pbj_model.audit.long_shifts.cols.date"),
				minWidth: 130,
				flex: 1,
				valueFormatter: (value: string) => formatReviewDate(value),
			},
			{
				field: "startTime",
				headerName: t("pbj_model.audit.long_shifts.cols.start"),
				minWidth: 180,
				flex: 1.4,
				valueFormatter: (value: string) => formatReviewDateTime(value, deptTimezone),
			},
			{
				field: "endTime",
				headerName: t("pbj_model.audit.long_shifts.cols.end"),
				minWidth: 180,
				flex: 1.4,
				valueFormatter: (value: string) => formatReviewDateTime(value, deptTimezone),
			},
			{
				field: "duration",
				headerName: t("pbj_model.audit.long_shifts.cols.duration"),
				minWidth: 130,
				flex: 1,
				type: "number",
				align: "left",
				headerAlign: "left",
				valueFormatter: (value: number) => (value == null ? "" : formatReviewHours(value)),
			},
		],
		[t, deptTimezone],
	);

	const sortingOptions = useMemo<MultiSortOption<ShiftDurationReviewRow>[] | undefined>(
		() =>
			sortingLocalStorageKey
				? [
						{ label: t("pbj_model.audit.long_shifts.cols.id"), sortColumns: ["empId"] },
						{ label: t("pbj_model.audit.long_shifts.cols.name"), sortColumns: ["name"] },
						{ label: t("pbj_model.audit.long_shifts.cols.date"), sortColumns: ["date", "startTime"] },
						{ label: t("pbj_model.audit.long_shifts.cols.start"), sortColumns: ["startTime"] },
						{ label: t("pbj_model.audit.long_shifts.cols.end"), sortColumns: ["endTime"] },
						{ label: t("pbj_model.audit.long_shifts.cols.duration"), sortColumns: ["duration"] },
					]
				: undefined,
		[t, sortingLocalStorageKey],
	);

	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<ShiftDurationReviewRow>[]>(
		() => [
			{ dimension: monthFacet<ShiftDurationReviewRow>("month", (r) => r.date), title: t("pbj_model.filter.month"), getLabel: labels.month, order: "key" },
			{ dimension: dayTypeFacet<ShiftDurationReviewRow>("day_type", (r) => r.date), title: t("pbj_model.filter.day_type"), getLabel: labels.dayType },
		],
		[t, labels],
	);
	const filters = useGridFacetFilters(rows, facets);
	const railHandoff = useFilterRailHandoff(filters.highlight, COLUMN_FACET, t("pbj_model.filter.in_rail"));

	return (
		<SettingsSection layout="stack" title={title} description={description} dataTestId={sectionTestId} sx={(theme) => sectionCardSx(theme)}>
			<FilterRailFrame
				flat
				height={sectionFrameHeight(rows.length)}
				dataTestId={`${tableTestId}-filters-frame`}
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId={`${tableTestId}-filters`}
					/>
				}
			>
				<SystemDataGrid<ShiftDurationReviewRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(r) => r.pbjShiftId}
					exportOptions={{ fileName: title }}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					localStorageKey={sortingLocalStorageKey}
					searchableColumns={["empId", "name"]}
					searchPlaceholder={t("pbj_model.audit.employees.search")}
					loading={loading}
					error={error}
					errorMessage={errorMessage}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={emptyTitle} dataTestId={emptyTestId} />}
							dataTestId={`${tableTestId}-filtered-empty`}
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId={tableTestId}
				/>
			</FilterRailFrame>
		</SettingsSection>
	);
};
